/**
 * Notification Hooks
 *
 * React Query hooks for the notification system.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  notificationsApi,
  type NotificationPreferences,
} from "@/lib/api";

// ═══════════════════════════════════════════════════════════════════
// Query Keys
// ═══════════════════════════════════════════════════════════════════

export const notificationKeys = {
  all: ["notifications"] as const,
  list: (params: { limit?: number; offset?: number; unreadOnly?: boolean }) =>
    [...notificationKeys.all, "list", params] as const,
  unreadCount: () => [...notificationKeys.all, "unreadCount"] as const,
  preferences: () => [...notificationKeys.all, "preferences"] as const,
};

// ═══════════════════════════════════════════════════════════════════
// Queries
// ═══════════════════════════════════════════════════════════════════

/**
 * Fetch notifications list with pagination.
 */
export function useNotifications(
  params: { limit?: number; offset?: number; unreadOnly?: boolean } = {}
) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const result = await notificationsApi.list(params);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta notifikationer");
      }
      return result.data!;
    },
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Fetch unread notification count.
 * Polls every 60 seconds by default.
 */
export function useUnreadNotificationCount(pollInterval = 60 * 1000) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const result = await notificationsApi.getUnreadCount();
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta antal olästa");
      }
      return result.data!.count;
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: pollInterval,
  });
}

/**
 * Fetch notification preferences.
 */
export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async () => {
      const result = await notificationsApi.getPreferences();
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte hämta preferenser");
      }
      return result.data!;
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// Mutations
// ═══════════════════════════════════════════════════════════════════

/**
 * Mark a notification as read.
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await notificationsApi.markAsRead(id);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte markera som läst");
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate all notification queries to refresh the data
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
    onMutate: async (_id) => {
      // Optimistic update for better UX
      await queryClient.cancelQueries({ queryKey: notificationKeys.all });

      // Update the unread count optimistically
      const previousCount = queryClient.getQueryData<number>(
        notificationKeys.unreadCount()
      );
      if (previousCount !== undefined && previousCount > 0) {
        queryClient.setQueryData(notificationKeys.unreadCount(), previousCount - 1);
      }

      return { previousCount };
    },
    onError: (_, __, context) => {
      // Revert optimistic update on error
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(
          notificationKeys.unreadCount(),
          context.previousCount
        );
      }
    },
  });
}

/**
 * Mark all notifications as read.
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await notificationsApi.markAllAsRead();
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte markera alla som lästa");
      }
      return result.data;
    },
    onSuccess: () => {
      // Reset unread count to 0
      queryClient.setQueryData(notificationKeys.unreadCount(), 0);
      // Invalidate list queries
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Delete a notification.
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const result = await notificationsApi.delete(id);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte ta bort notifikation");
      }
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Update notification preferences.
 */
export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates: Partial<NotificationPreferences>) => {
      const result = await notificationsApi.updatePreferences(updates);
      if (!result.success) {
        throw new Error(result.error?.message || "Kunde inte spara preferenser");
      }
      return result.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(notificationKeys.preferences(), data);
    },
  });
}
